import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import debounce from 'lodash.debounce';
import { ANIMATION_CLASSES, BREAKPOINTS } from '@/js/lib/constants';
import { TRIGGER_START } from './base';

gsap.registerPlugin(ScrollTrigger);

const els = document.querySelectorAll<HTMLElement>('.js-animate-heading');
const lineClass = 'animate-line';
const restictedLanguages = ['zh-Hans'];

const wrapString = (
  text: HTMLElement,
  highlights: NodeListOf<HTMLSpanElement>
) => {
  // Wrap the content in a span tag for highlighting
  if (!highlights.length) return;

  const lines = text.querySelectorAll<HTMLElement>(`.${lineClass}__inner`);
  const wrap = (line: HTMLElement, text: string) => {
    line.innerHTML = line.innerHTML.replace(text, `<span>${text}</span>`);
  };

  highlights.forEach(highlight => {
    const searchString = highlight.innerHTML;
    const words = searchString.split(/\s+/g);

    // Iterate over each line looking for the highlighted string
    for (const line of Array.from(lines)) {
      if (line.innerHTML.includes(searchString)) {
        wrap(line, searchString);
        break;
      }

      // If string is split onto multiple lines, the search text is split
      // and highlighted individually
      words.forEach(word => {
        if (!line.innerHTML.includes(word)) return;

        wrap(line, word);
      });
    }
  });
};

const splitTextByLines = (text: HTMLElement) => {
  const highlights = text.querySelectorAll('span');
  // Replace new line characters with an empty div for manually placed new lines (note <br /> will not work here)
  text.innerHTML = text.innerHTML
    .trim()
    .replace(/<\/?span[^>]*>/g, '') // Remove span tags (these are added back in later)
    .replace(/\r?\n|\r|\n/g, ' <div></div>');

  // Split text into words
  const words = (text.innerText || text.innerHTML).split(/(\s+)/);

  // Replace text with 'words' wrapped in custom tags
  const formattedWords = words
    .map(word => {
      return `<word>${word}</word>`;
    })
    .join();

  text.innerHTML = formattedWords;

  const wordEls = text.querySelectorAll<HTMLElement>('word'),
    lines: string[] = [],
    line: HTMLElement[] = [];
  let lineOffset;

  const stripLine = (line: HTMLElement) => line.innerHTML; // Remove tags from the words
  const commitLine = (line: HTMLElement[]) => {
    const formattedLine = line.map(stripLine).reduce((acc, cur) => {
      const accSplit = acc.split('');

      // Check if the current word, or previous character, contains a hyphen
      if (cur.includes('-') || accSplit.pop()?.includes('-')) return acc + cur;
      return `${acc} ${cur}`;
    }, '');

    lines.push(formattedLine);
  };

  for (const word of Array.from(wordEls)) {
    // Iterate through the words determing if they start
    // on a new line based on their top offset value
    const { top } = word.getBoundingClientRect();

    if (top !== lineOffset && lineOffset !== undefined) {
      commitLine(line);
      line.length = 0;
    }

    line.push(word);
    lineOffset = top;
  }
  commitLine(line); // Needed to commit the final line not pushed in the loop

  // Replace the original text with seperated and wrapped lines
  text.innerHTML = lines
    .map(
      line =>
        `<div class="${lineClass}"><span class="${lineClass}__inner">${line}</span></div>`
    )
    .join('');

  wrapString(text, highlights);
};

const bindAnimation = (el: HTMLElement) => {
  const innerLines = el.querySelectorAll(`.${lineClass}__inner`);

  gsap.set(innerLines, {
    y: '100%',
  });

  el.classList.add(ANIMATION_CLASSES.rigged);

  gsap.to(innerLines, {
    scrollTrigger: {
      trigger: el,
      start: TRIGGER_START,
    },
    ease: 'custom-ease',
    y: 0,
    duration: 0.6,
    stagger: 0.1,
  });
};

function removeAnimationClasses(el: HTMLElement) {
  el.className = Array.from(el.classList)
    .filter((className: string) => !className.includes('animate'))
    .join(' ');
}

function animateHeading(el: HTMLElement) {
  const lang = document.documentElement.getAttribute('lang');

  if (restictedLanguages.includes(lang)) return removeAnimationClasses(el);

  const initialContent = el.innerHTML;
  let initialised = false;

  const init = () => {
    if (initialised) el.innerHTML = initialContent;

    splitTextByLines(el);
    bindAnimation(el);
    initialised = true;
  };
  const debouncedInit = debounce(init, 250);

  window.addEventListener('resize', () => {
    if (window.innerWidth < BREAKPOINTS.sm) return; // Prevent mobile dynamic address bar triggering resize event on scroll
    debouncedInit();
  });
  init();
}

els.forEach(animateHeading);
